class SessionManager {
  constructor() {
    this.SESSION_TIMEOUT = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
    this.WARNING_TIME = 5 * 60 * 1000; // 5 minutes warning in milliseconds
    this.activityTimer = null;
    this.warningTimer = null;
    this.isActive = false;
    this.onWarning = null;
    this.onLogout = null;
    this.lastActivity = Date.now();
    
    // Bind methods to preserve context
    this.trackActivity = this.trackActivity.bind(this);
    this.startSession = this.startSession.bind(this);
    this.endSession = this.endSession.bind(this);
    this.extendSession = this.extendSession.bind(this);
    this.checkSession = this.checkSession.bind(this);
  }

  // Initialize session tracking
  startSession(onWarning = null, onLogout = null) {
    this.onWarning = onWarning;
    this.onLogout = onLogout;
    this.isActive = true;
    this.lastActivity = Date.now();
    
    // Store session start time
    localStorage.setItem('sessionStartTime', Date.now().toString());
    localStorage.setItem('lastActivity', Date.now().toString());
    
    // Set up activity tracking
    this.setupActivityTracking();
    
    // Start the session timer
    this.resetTimer();
    
    console.log('Session started with 3-hour timeout');
  }

  // Set up event listeners for user activity
  setupActivityTracking() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, this.trackActivity, true);
    });
  }

  // Remove activity tracking event listeners
  removeActivityTracking() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.removeEventListener(event, this.trackActivity, true);
    });
  }

  // Track user activity
  trackActivity() {
    if (!this.isActive) return;
    
    const now = Date.now();
    this.lastActivity = now;
    localStorage.setItem('lastActivity', now.toString());
    
    // Reset the timer on activity
    this.resetTimer();
  }

  // Reset the session timer
  resetTimer() {
    // Clear existing timers
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
    }

    // Set warning timer (5 minutes before logout)
    this.warningTimer = setTimeout(() => {
      if (this.isActive && this.onWarning) {
        this.onWarning();
      }
    }, this.SESSION_TIMEOUT - this.WARNING_TIME);

    // Set logout timer (3 hours)
    this.activityTimer = setTimeout(() => {
      if (this.isActive) {
        this.performLogout();
      }
    }, this.SESSION_TIMEOUT);
  }

  // Extend session (called when user clicks "Stay Logged In")
  extendSession() {
    this.lastActivity = Date.now();
    localStorage.setItem('lastActivity', Date.now().toString());
    this.resetTimer();
    console.log('Session extended');
  }

  // Check if session is still valid
  checkSession() {
    const sessionStart = parseInt(localStorage.getItem('sessionStartTime') || '0');
    const lastActivity = parseInt(localStorage.getItem('lastActivity') || '0');
    const isLoggedIn = localStorage.getItem('adminLoggedIn') === 'true';
    
    console.log('Session check:', { 
      isLoggedIn, 
      sessionStart, 
      lastActivity, 
      now: Date.now(),
      timeSinceLastActivity: lastActivity ? Date.now() - lastActivity : 'N/A'
    });
    
    // If not logged in, definitely invalid
    if (!isLoggedIn) {
      console.log('Session invalid: not logged in');
      return false;
    }
    
    // If no session timestamps exist, this is an old/invalid session
    if (!sessionStart || !lastActivity) {
      console.log('Session invalid: no session timestamps');
      this.clearInvalidSession();
      return false;
    }
    
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivity;
    
    // If more than 3 hours since last activity, session is expired
    if (timeSinceLastActivity > this.SESSION_TIMEOUT) {
      console.log('Session expired: timeout exceeded');
      this.performLogout();
      return false;
    }
    
    console.log('Session valid');
    return true;
  }

  // Clear invalid session data
  clearInvalidSession() {
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminName');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminDealershipId');
    localStorage.removeItem('sessionStartTime');
    localStorage.removeItem('lastActivity');
    console.log('Invalid session data cleared');
  }

  // Perform logout
  performLogout() {
    console.log('Session expired - logging out');
    this.endSession();
    
    // Clear all authentication data
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminName');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminDealershipId');
    localStorage.removeItem('sessionStartTime');
    localStorage.removeItem('lastActivity');
    
    // Call custom logout handler if provided
    if (this.onLogout) {
      this.onLogout();
    } else {
      // Default logout behavior
      window.location.replace('/admin/login');
    }
  }

  // End session tracking
  endSession() {
    this.isActive = false;
    
    // Clear timers
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
      this.activityTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    
    // Remove event listeners
    this.removeActivityTracking();
    
    // Clear session data
    localStorage.removeItem('sessionStartTime');
    localStorage.removeItem('lastActivity');
    
    console.log('Session ended');
  }

  // Get remaining session time
  getRemainingTime() {
    const lastActivity = parseInt(localStorage.getItem('lastActivity') || '0');
    if (!lastActivity) return 0;
    
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivity;
    const remaining = this.SESSION_TIMEOUT - timeSinceLastActivity;
    
    return Math.max(0, remaining);
  }

  // Format remaining time for display
  formatRemainingTime() {
    const remaining = this.getRemainingTime();
    const hours = Math.floor(remaining / (60 * 60 * 1000));
    const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
    const seconds = Math.floor((remaining % (60 * 1000)) / 1000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  // Manual logout (called when user clicks logout button)
  logout() {
    console.log('Manual logout');
    this.performLogout();
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

export default sessionManager; 